import { Box, Stack } from "@mui/material";
import { BodyTextM } from "@repo/ui";
import { type FC, type ReactNode } from "react";
import { InfoTooltip } from "../Tooltip";

interface DescriptionProps {
	description: Exclude<string | string[] | ReactNode, null | undefined>;
	swipeable?: boolean;
	highlighted?: boolean;
	selector?: string;
}

const Description: FC<DescriptionProps> = ({
	description,
	swipeable,
	highlighted,
	selector,
}) => {
	if (Array.isArray(description)) {
		return (
			<>
				{description.map((item) => {
					return (
						<BodyTextM
							key={item}
							sx={{ overflowWrap: "anywhere" }}
							data-test={selector}
						>
							{item}
						</BodyTextM>
					);
				})}
			</>
		);
	} else if (typeof description === "string") {
		return (
			<BodyTextM
				sx={{
					color: highlighted
						? { md: "primary.main" }
						: swipeable && "grey.dark",
					overflowWrap: "anywhere",
				}}
				data-test={selector}
			>
				{description}
			</BodyTextM>
		);
	}
	return (
		<BodyTextM
			sx={{ width: "100%", overflowWrap: "anywhere" }}
			data-test={selector}
		>
			{description}
		</BodyTextM>
	);

}

export interface HeaderWithDescriptionProps extends DescriptionProps {
	title: string | ReactNode;
	subTitle?: string | ReactNode;
	textColor?: string;
	additionalElements?: ReactNode[] | ReactNode;
	highlighted?: boolean;
	valueSelector?: string;
	secondRow?: string | ReactNode;
	tooltipInformation?: string;
	alignDesecription?: "normal" | "right";
}

const HeaderWithDescription: FC<HeaderWithDescriptionProps> = ({
	title,
	subTitle,
	description,
	swipeable,
	additionalElements = <></>,
	highlighted,
	valueSelector,
	secondRow,
	tooltipInformation,
	alignDesecription = "normal",
	...rest
}) => {
	return (
		<Stack>
			<Stack
				{...rest}
				sx={{
					flexDirection: {
						justifyContent:
							alignDesecription === "right" ? "space-between" : "flex-start",
						md: "row",
					},
				}}
			>
				<Stack
					sx={{
						display: { md: swipeable ? "none" : "block" },
						width: { md: alignDesecription === "normal" ? "40%" : "auto" },
						pr: { md: 4 },
						color: (theme) =>
							swipeable
								? theme.palette.grey.ultraDark
								: theme.palette.grey.dark,
					}}
				>
					<Stack sx={{ flexDirection: "row", gap: 2 }}>
						{title && (
							<BodyTextM
								strong
								sx={{
									display: tooltipInformation ? "inline-block" : "block",
									flexGrow: 1,
								}}
							>
								{title}
							</BodyTextM>
						)}
						{tooltipInformation && (
							<InfoTooltip
								title={tooltipInformation}
								sx={{ alignSelf: "flex-start" }}
							/>
						)}
					</Stack>
					<Stack>{subTitle && <BodyTextM>{subTitle}</BodyTextM>}</Stack>
				</Stack>
				<Stack
					sx={{
						flexDirection: { md: "row" },
						justifyContent: { md: "space-between" },
						width: {
							md: !title || alignDesecription === "right" ? "auto" : "60%",
						},
						".MuiButton-root": {
							p: 0,
						},
					}}
				>
					<Description
						description={description}
						swipeable={swipeable}
						highlighted={highlighted}
						selector={valueSelector}
					/>
					<Box>
						{/* <Box> prevents full width stretch of inside elements */}
						{Array.isArray(additionalElements)
							? additionalElements.map((item) => {
								return item;
							})
							: additionalElements}
					</Box>
				</Stack>
			</Stack>
			{secondRow}
		</Stack>
	);
}

export const SubHeaderWithDescription: FC<HeaderWithDescriptionProps> = ({
	title,
	description,
	textColor = undefined,
	...rest
}) => {
	return (
		<Stack {...rest}>
			<BodyTextM strong sx={{ color: textColor }}>
				{title}
			</BodyTextM>
			<BodyTextM>{description}</BodyTextM>
		</Stack>
	);
}

export default HeaderWithDescription;
